/** Wire format version prefix for encrypted note bodies. */
export const NOTES_CIPHER_VERSION = "v1";
/** PBKDF2 iterations (OWASP 2023 recommendation for SHA-256). */
export const PBKDF2_ITERATIONS = 600_000;
const VERIFIER_PLAINTEXT = "trove-notes-lock-v1";
const SALT_BYTES = 16;
const IV_BYTES = 12;
function getSubtle() {
    const subtle = globalThis.crypto?.subtle;
    if (!subtle) {
        throw new Error("Web Crypto API (crypto.subtle) is not available in this environment.");
    }
    return subtle;
}
function bytesToBase64(bytes) {
    let binary = "";
    for (const b of bytes)
        binary += String.fromCharCode(b);
    return btoa(binary);
}
function base64ToBytes(b64) {
    const binary = atob(b64);
    const out = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++)
        out[i] = binary.charCodeAt(i);
    return out;
}
/** Random salt for key derivation (base64). */
export function generateNotesLockSalt() {
    const salt = new Uint8Array(SALT_BYTES);
    crypto.getRandomValues(salt);
    return bytesToBase64(salt);
}
/** Derive AES-256-GCM key from Notes lock password + per-user salt. */
export async function deriveNotesLockKey(password, saltB64) {
    const subtle = getSubtle();
    const enc = new TextEncoder();
    const keyMaterial = await subtle.importKey("raw", enc.encode(password), "PBKDF2", false, ["deriveKey"]);
    return subtle.deriveKey({
        name: "PBKDF2",
        salt: base64ToBytes(saltB64),
        iterations: PBKDF2_ITERATIONS,
        hash: "SHA-256",
    }, keyMaterial, { name: "AES-GCM", length: 256 }, false, ["encrypt", "decrypt"]);
}
/** Encrypt note body HTML → `v1.<iv-b64>.<ciphertext+tag-b64>`. */
export async function encryptNoteBody(plaintextHtml, key) {
    const subtle = getSubtle();
    const iv = new Uint8Array(IV_BYTES);
    crypto.getRandomValues(iv);
    const ct = await subtle.encrypt({ name: "AES-GCM", iv: iv }, key, new TextEncoder().encode(plaintextHtml));
    return `${NOTES_CIPHER_VERSION}.${bytesToBase64(iv)}.${bytesToBase64(new Uint8Array(ct))}`;
}
/** Decrypt `notesCipher` wire string → plaintext HTML. */
export async function decryptNoteBody(notesCipher, key) {
    if (!isValidNotesCipher(notesCipher)) {
        throw new Error("Invalid or unsupported notesCipher format.");
    }
    const parts = notesCipher.split(".");
    const iv = base64ToBytes(parts[1]);
    const combined = base64ToBytes(parts[2]);
    const subtle = getSubtle();
    const pt = await subtle.decrypt({ name: "AES-GCM", iv: iv }, key, combined);
    return new TextDecoder().decode(pt);
}
/** Create password verifier blob (encrypted fixed token). */
export async function createNotesLockVerifier(password, saltB64) {
    const key = await deriveNotesLockKey(password, saltB64);
    return encryptNoteBody(VERIFIER_PLAINTEXT, key);
}
/** Verify Notes lock password against stored verifier. */
export async function verifyNotesLockPassword(password, saltB64, verifierCipher) {
    try {
        const key = await deriveNotesLockKey(password, saltB64);
        const pt = await decryptNoteBody(verifierCipher, key);
        return pt === VERIFIER_PLAINTEXT;
    }
    catch {
        return false;
    }
}
/** Validate persisted cipher wire format (server-side guard). */
export function isValidNotesCipher(value) {
    if (typeof value !== "string" || !value.startsWith(`${NOTES_CIPHER_VERSION}.`))
        return false;
    const parts = value.split(".");
    if (parts.length !== 3)
        return false;
    try {
        const iv = base64ToBytes(parts[1]);
        const ct = base64ToBytes(parts[2]);
        return iv.length === IV_BYTES && ct.length >= 16;
    }
    catch {
        return false;
    }
}
